/**
 * Hook owning the `complete-intake` activity-completion chain used by both
 * intake forms.
 *
 * After an intake encounter is saved, the chain runs:
 *   1. handleActivityCompletion("complete-intake")
 *   2. (if user selected enroll-now)
 *      a. submitProgramSelection(...)
 *      b. handleActivityCompletion("finalize-care-plan-enrollment")
 *
 * This is the post-save block from `Encounter.form.tsx:877-957`, lifted as
 * a pure helper so the typed intake forms compose it directly.
 *
 * @agentic connections
 */

import { useCallback } from "react";
import type { MedplumClient } from "@medplum/core";
import type { Encounter, Patient, PractitionerRole } from "@medplum/fhirtypes";

import { handleActivityCompletion } from "@benchmark/core/workflows";
import { getEncounterCompletionLabel } from "@benchmark/core/resolvers";
import { useProgramSelection } from "@/hooks/domain/useProgramSelection";
import type {
  ClinicalIntakeInteractionFormData,
  CoCMIntakeInteractionFormData,
  EncounterActivityContext,
} from "@benchmark/core/types";

/** Intake form shapes that drive the chain. @agentic connections */
type IntakeFormData =
  ClinicalIntakeInteractionFormData | CoCMIntakeInteractionFormData;

/**
 * Outcome of {@link UseCompleteIntakeChainResult.run}.
 *
 * @agentic connections
 */
export type CompleteIntakeChainOutcome =
  | { status: "success"; message: string }
  | { status: "warning"; message: string };

/**
 * Inputs to {@link useCompleteIntakeChain}.
 *
 * @agentic connections
 */
export type UseCompleteIntakeChainOptions = {
  patientId: string | undefined;
  carePlanId: string | undefined;
};

/**
 * Returned API of {@link useCompleteIntakeChain}.
 *
 * @agentic connections
 */
export type UseCompleteIntakeChainResult = {
  run: (args: {
    medplum: MedplumClient;
    encounter: Encounter;
    formData: IntakeFormData;
    patient: Patient;
    practitionerRole: PractitionerRole | undefined;
    activityContext: EncounterActivityContext;
  }) => Promise<CompleteIntakeChainOutcome>;
};

/**
 * Returns a stable `run` callback that performs the activity-completion
 * chain after an intake encounter is saved.
 *
 * @agentic connections
 */
export const useCompleteIntakeChain = ({
  patientId,
  carePlanId,
}: UseCompleteIntakeChainOptions): UseCompleteIntakeChainResult => {
  const { submit: submitProgramSelection } = useProgramSelection(
    patientId,
    carePlanId,
  );

  const run = useCallback<UseCompleteIntakeChainResult["run"]>(
    async ({
      medplum,
      encounter,
      formData,
      patient,
      practitionerRole,
      activityContext,
    }) => {
      const carePlan = await medplum.readResource(
        "CarePlan",
        activityContext.carePlanId,
      );

      await handleActivityCompletion(medplum, {
        carePlan,
        activityCode: activityContext.activityCode,
        statusReason: `Completed via ${getEncounterCompletionLabel(encounter)}`,
        ...(encounter.id && {
          documentedEncounter: {
            id: encounter.id,
            display: getEncounterCompletionLabel(encounter),
          },
        }),
        completionData: {
          nextActivityOwnerRef: formData["followup-assigned-to"] || undefined,
          nextActivityOwnerDisplay:
            formData["followup-assigned-to-display"] || undefined,
        },
        practitionerRole,
      });

      const enrollmentSelection = formData["followup-program-selection"];
      const shouldChainEnrollment =
        activityContext.activityCode === "complete-intake" &&
        formData["followup-enroll"] === "enroll-now";

      if (!shouldChainEnrollment || !enrollmentSelection) {
        return {
          status: "success",
          message:
            "Encounter saved and activity completed. Next step advanced.",
        };
      }

      try {
        if (!practitionerRole) {
          return {
            status: "success",
            message:
              "Encounter saved and activity completed. Next step advanced.",
          };
        }

        const updatedCarePlan = await medplum.readResource(
          "CarePlan",
          activityContext.carePlanId,
        );

        const selectedProgram = enrollmentSelection.selectedProgram;
        const includeReferrals =
          selectedProgram === "triage" ||
          (selectedProgram === "cocm" &&
            formData["followup-include-referrals"] === true);
        const includedServiceNeeds = includeReferrals
          ? (formData["followup-service-needs"] ?? [])
          : [];
        const firstActivityOwnerOverride =
          formData["followup-assigned-to"] &&
          formData["followup-assigned-to-display"]
            ? {
                ref: formData["followup-assigned-to"],
                display: formData["followup-assigned-to-display"],
              }
            : undefined;
        const programData = {
          ...enrollmentSelection,
          serviceNeeds: includedServiceNeeds,
          selectedNeeds: includedServiceNeeds
            .map((n) => n.serviceType)
            .filter(Boolean),
          requiresApproval:
            includeReferrals &&
            (formData["followup-requires-approval"] ?? true),
        };

        await submitProgramSelection(
          programData,
          patient,
          practitionerRole,
          updatedCarePlan,
          firstActivityOwnerOverride,
        );

        const carePlanAfterEnrollment = await medplum.readResource(
          "CarePlan",
          activityContext.carePlanId,
        );
        await handleActivityCompletion(medplum, {
          carePlan: carePlanAfterEnrollment,
          activityCode: "finalize-care-plan-enrollment",
          statusReason: `Completed via enrollment chaining after ${getEncounterCompletionLabel(encounter)}`,
          practitionerRole,
        });

        return {
          status: "success",
          message: "Encounter saved, intake completed, and member enrolled.",
        };
      } catch (enrollmentError) {
        console.error("Enrollment chaining failed:", enrollmentError);
        return {
          status: "warning",
          message:
            "Encounter saved and intake completed, but enrollment failed. Please complete enrollment from the task list.",
        };
      }
    },
    [submitProgramSelection],
  );

  return { run };
};
